import type { Tenant, TenantSignupInput } from '@detect/shared';
import { api, type PaginatedResult } from './api';
import type { WithId } from './types';

/**
 * Platform-level surfaces (no tenant context): self-serve signup
 * (desk-integration.md §9) and the superadmin tenants list.
 */
/**
 * What the Meshtek Platform Directory knows about the signed-in user's
 * own organization (auth#5). The domain is derived server-side from the
 * caller's verified email — never sent from here, so this can't be used
 * to ask about anyone else's company.
 */
export interface PlatformPresence {
  domain: string | null;
  eligible: boolean;
  reason: 'consumer_email_domain' | 'email_not_verified' | null;
  found: boolean;
  products: {
    product: 'desk' | 'dispatch' | 'gendesk';
    /** Opaque storage key, sent for Dispatch ONLY (the id the customer pastes). Never present for Desk (server-side only). */
    productTenantId?: string;
    /** The identifier that product shows ITS customers; null if it has none. */
    productSlug: string | null;
    displayName: string | null;
  }[];
}

export const platformService = {
  /** Pre-fill source for the signup wizard: which family products this
   *  organization already uses. Advisory — never connects anything. */
  platformPresence: (): Promise<PlatformPresence> => api.get('/signup/platform'),

  /** Customer self-service: create your organization. */
  signup: (
    input: TenantSignupInput
  ): Promise<WithId<Tenant> & { membership: { role: string } }> =>
    api.post('/signup/tenants', input),

  /** Superadmin platform panel: every tenant, id-cursored. */
  listTenants: (
    params: { limit?: number; startAfter?: string } = {}
  ): Promise<PaginatedResult<WithId<Tenant>>> => {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.startAfter !== undefined)
      query.set('startAfter', params.startAfter);
    const qs = query.toString();
    return api.getPaginated(`/provision/tenants${qs ? `?${qs}` : ''}`);
  },
};
